import React, { useState, useEffect, useRef } from "react";
import "./ProductDetail127.css";

/* ── Intersection Observer Hook ── */
function useInView(threshold = 0.10) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const STEPS = [
  { num: "01", icon: "bi-sign-intersection-side", title: "Vehicle Enters Lane",  desc: "Loop sensors & AI cameras activate as vehicle approaches the toll plaza." },
  { num: "02", icon: "bi-camera2",                title: "AI Camera Captures",   desc: "High-speed camera captures full vehicle image with frame precision." },
  { num: "03", icon: "bi-upc-scan",               title: "OCR + LPR Detection",  desc: "OCR reads license plate; AI classifies vehicle type and axle count." },
  { num: "04", icon: "bi-credit-card",            title: "FASTag Verification",  desc: "RFID scans FASTag; cross-validates against the live cloud database." },
  { num: "05", icon: "bi-check-circle",           title: "Exemption Check",      desc: "Vehicle matched against exemption & blacklist databases instantly." },
  { num: "06", icon: "bi-cloud-upload",           title: "Cloud Data Storage",   desc: "All data timestamped in secure, encrypted cloud database." },
  { num: "07", icon: "bi-speedometer2",           title: "Dashboard Update",     desc: "Live dashboard refreshes; boom barrier cleared; reports updated." },
];

const ARCH_LAYERS = [
  { label: "EDGE LAYER",   icon: "bi-camera-video-fill", title: "Field Hardware",   color: "#0284c7", lightBg: "#e0f2fe", items: ["AI ANPR Cameras","Loop Sensors","RFID Readers","Boom Barriers","LED Displays"] },
  { label: "AI ENGINE",    icon: "bi-cpu-fill",           title: "Processing Core",  color: "#0284c7", lightBg: "#e0f2fe", items: ["OCR / LPR Engine","Axle Count AI","FASTag Validator","Fraud Engine","Real-Time Alerts"] },
  { label: "CLOUD LAYER",  icon: "bi-cloud-fill",         title: "Data Platform",    color: "#0284c7", lightBg: "#e0f2fe", items: ["Encrypted Storage","Multi-Plaza Sync","Event Archive","API Gateway","Auto Backup"] },
  { label: "DASHBOARD",    icon: "bi-display-fill",       title: "Command Center",   color: "#0284c7", lightBg: "#e0f2fe", items: ["Live Analytics","Revenue Reports","Audit Exports","Role-Based Access","Mobile App"] },
];

const SPECS = [
  { icon: "bi-lightning-charge-fill", label: "Response",   val: "< 200ms",   color: "#0284c7" },
  { icon: "bi-shield-check-fill",     label: "Uptime SLA", val: "99.9%",     color: "#0284c7" },
  { icon: "bi-hdd-network-fill",      label: "Encryption", val: "AES-256",   color: "#0284c7" },
  { icon: "bi-arrows-expand",         label: "Scale",      val: "2–50 Lanes",color: "#0284c7" },
  { icon: "bi-clock-history",         label: "Data Logs",  val: "5 Years",   color: "#0284c7" },
  { icon: "bi-broadcast",             label: "Monitoring", val: "24 × 7",    color: "#0284c7" },
];

/* ═══ HOW IT WORKS ═══ */
function HowItWorksSection() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [secRef, inView] = useInView();

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive(s => (s + 1) % STEPS.length), 2700);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section className="tpf-how" ref={secRef}>
      <div className="tpf-how-blob1" />
      <div className="tpf-how-blob2" />

      <div className="tpf-wrap">

        <div className={`tpf-head ${inView ? "tpf-head-in" : ""}`}>
          <span className="tpf-eyebrow">HOW IT WORKS</span>
          <h2 className="tpf-h2">
            From Vehicle Approach <span>to Full Clearance</span>
          </h2>
          {/* <p className="tpf-sub">
            7 intelligent AI-powered steps — completed in under <strong>21 seconds</strong>
          </p> */}
        </div>

        {/* Progress tracker */}
        <div className={`tpf-progress ${inView ? "tpf-progress-in" : ""}`}>
          <div className="tpf-progress-track">
            <div className="tpf-progress-fill" style={{ width: `${((active + 1) / STEPS.length) * 100}%` }} />
          </div>
          <span className="tpf-progress-tag">Step {active + 1} / {STEPS.length}</span>
        </div>

        {/* Step cards */}
        <div className="tpf-grid">
          {STEPS.map((s, i) => (
            <div
              key={i}
              className={`tpf-card ${active === i ? "tpf-card-on" : ""} ${inView ? "tpf-card-in" : ""}`}
              style={{ animationDelay: `${i * 65}ms` }}
              onMouseEnter={() => { setPaused(true); setActive(i); }}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="tpf-card-topbar" />
              {/* <div className="tpf-card-num">{s.num}</div> */}
              <div className="tpf-card-icon">
                <i className={`bi ${s.icon}`} />
              </div>
              <h4 className="tpf-card-title">{s.title}</h4>
              <p className="tpf-card-desc">{s.desc}</p>
              {i < STEPS.length - 1 && <div className="tpf-card-arrow">›</div>}
            </div>
          ))}
        </div>

        {/* Dot indicators */}
        <div className="tpf-dots">
          {STEPS.map((_, i) => (
            <button
              key={i}
              className={`tpf-dot ${active === i ? "tpf-dot-on" : ""}`}
              onClick={() => { setActive(i); setPaused(true); }}
            />
          ))}
        </div>

        {/* Timer badge */}
        {/* <div className={`tpf-timerbadge ${inView ? "tpf-timerbadge-in" : ""}`}>
          <i className="bi bi-stopwatch-fill" />
          <span>Average end-to-end clearance time:</span>
          <strong>≤ 21 Seconds</strong>
        </div> */}

      </div>
    </section>
  );
}

/* ═══ ARCHITECTURE ═══ */
function ArchitectureSection() {
  const [hovered, setHovered] = useState(null);
  const [secRef, inView] = useInView();

  return (
    <section className="tpf-arch " ref={secRef}>
      <div className="tpf-arch-blob1" />
      <div className="tpf-arch-blob2" />

      <div className="tpf-wrap">

        <div className={`tpf-head ${inView ? "tpf-head-in" : ""}`}>
          <span className="tpf-eyebrow">SYSTEM ARCHITECTURE</span>
          <h2 className="tpf-h2">
            Cloud-Native &amp; Scalable <span>End-to-End Architecture</span>
          </h2>
          <p className="tpf-sub">
            From edge cameras at the plaza to secure cloud storage and operator dashboard —
            every component is connected, redundant, and failsafe.
          </p>
        </div>

        {/* Architecture image */}
        <div className={`tpf-archimg ${inView ? "tpf-archimg-in" : ""}`}>
          <img src="ImagesNew/fLOW-LR.png" alt="TPMS system architecture" />
          <div className="tpf-archimg-fade" />
          <div className="tpf-archimg-badge">
            <i className="bi bi-patch-check-fill" /> NHAI Verified System
          </div>
        </div>

        {/* Layer cards with connector */}
        <div className="tpf-layers-wrap">
          {/* horizontal connector line */}
          <div className="tpf-hline">
            <div className="tpf-hline-glow" />
            {[0,1,2].map(i => (
              <div key={i} className="tpf-hline-arrow" style={{ left: `${(i + 1) * 25}%` }}>
                <svg viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="#0284c7" strokeWidth="1.5" fill="white" />
                  <path d="M7 10h6M11 7l3 3-3 3" stroke="#0284c7" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            ))}
          </div>

          <div className="tpf-layers">
            {ARCH_LAYERS.map((layer, i) => (
              <div
                key={i}
                className={`tpf-layer ${hovered === i ? "tpf-layer-on" : ""} ${inView ? "tpf-layer-in" : ""}`}
                style={{
                  animationDelay: `${i * 100}ms`,
                  "--lc": layer.color,
                  "--lb": layer.lightBg,
                }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="tpf-layer-chip">{layer.label}</div>

                <div className="tpf-layer-icon">
                  <i className={`bi ${layer.icon}`} />
                </div>

                <h4 className="tpf-layer-title">{layer.title}</h4>

                <ul className="tpf-layer-list">
                  {layer.items.map((item, j) => (
                    <li key={j}>
                      <span className="tpf-layer-dot" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="tpf-layer-wm">0{i + 1}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Specs strip */}
        {/* <div className={`tpf-specs-strip ${inView ? "tpf-specs-in" : ""}`}>
          <div className="tpf-specs-heading">
            <i className="bi bi-bar-chart-line-fill" /> System Performance Highlights
          </div>
          <div className="tpf-specs-row">
            {SPECS.map((sp, i) => (
              <div
                key={i}
                className="tpf-speccard"
                style={{ "--sc": sp.color, animationDelay: `${i * 80}ms` }}
              >
                <div className="tpf-speccard-icon">
                  <i className={`bi ${sp.icon}`} />
                </div>
                <strong className="tpf-speccard-val">{sp.val}</strong>
                <span className="tpf-speccard-label">{sp.label}</span>
              </div>
            ))}
          </div>
        </div> */}

      </div>
    </section>
  );
}

 function ProductDetail127() {
  return (
    <>
      <HowItWorksSection />
      <ArchitectureSection />
    </>
  );
}
export default ProductDetail127;